/**
 * ALL CAPS — Find Your Cap quiz content and scoring rules.
 *
 * THIS IS THE ONE FILE TO EDIT to change quiz questions, answers, or scoring.
 * Nothing else in the codebase needs to change when you edit this file.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * HOW THE MATCHING SYSTEM WORKS
 * ─────────────────────────────────────────────────────────────────────────
 * Every product carries TRAITS, parsed at render time from its Shopify tags:
 *   - category  <- from a tag like "allcaps-office"      (one per product)
 *   - tone      <- from a tag like "trait-tone-dry"
 *   - risk      <- from a tag like "trait-risk-medium"
 *   - energy    <- from a tag like "trait-energy-wearable"
 *
 * Each quiz answer below adds points to one or more of those same axes via
 * its `scores` object. After the last question, we sum the player's points
 * per axis/value, then score every real product by how well its own traits
 * line up with those totals. Highest score wins; ties break toward the
 * lower Shopify product id (oldest/flagship product in that group), so the
 * result is always deterministic — the same answers always produce the same
 * recommendation, never a random pick.
 *
 * TO ADD A NEW TRAIT AXIS (e.g. "material"):
 *   1. Tag products with "trait-material-wool" etc in Shopify.
 *   2. Reference `material` in a question's answer `scores` below.
 *   That's it — the scoring loop in ac-find-your-cap.js reads whatever axes
 *   appear in `scores`, it doesn't hardcode axis names.
 *
 * TO ADD/REMOVE/REORDER QUESTIONS: edit the `questions` array. Each question
 * needs exactly 4 answers to keep the 2x2 answer-card grid from Concept 9
 * intact, but that's a design constraint, not a code one.
 *
 * TO CHANGE HOW MUCH AN AXIS MATTERS: edit AXIS_WEIGHTS below. Category is
 * weighted highest because it's the strongest, clearest signal (mirrors how
 * Concept 9's own original logic leaned on category-like lanes first).
 */
window.ACQuizData = {
  axisWeights: {
    category: 2,
    tone: 1,
    risk: 1,
    energy: 1,
  },

  questions: [
    {
      id: 'vibe',
      title: "What's your general vibe?",
      text: 'Pick the one that feels closest.',
      answers: [
        {
          label: 'Professionally difficult',
          sub: 'Slack status permanently away',
          scores: { category: { office: 2 }, tone: { dry: 1 } },
        },
        {
          label: 'Romantically messy',
          sub: 'You love a text with consequences',
          scores: { category: { dating: 2 }, tone: { flirty: 1 } },
        },
        {
          label: 'Public chaos',
          sub: 'Neighbourhood main character energy',
          scores: { category: { chaos: 2 }, tone: { chaotic: 1 } },
        },
        {
          label: 'Family issue',
          sub: 'You already know why',
          scores: { category: { family: 2 }, tone: { dry: 1 } },
        },
      ],
    },
    {
      id: 'wear',
      title: 'Where are you most likely to wear this?',
      text: 'Be honest.',
      answers: [
        {
          label: 'Work event',
          sub: 'Unwise but memorable',
          scores: { category: { office: 1 }, energy: { statement: 1 } },
        },
        {
          label: 'Date or breakup brunch',
          sub: 'Two sides of the same coin',
          scores: { category: { dating: 1 }, tone: { deadpan: 1 } },
        },
        {
          label: 'At the pub',
          sub: 'No notes',
          scores: { category: { chaos: 1 }, energy: { wearable: 1 } },
        },
        {
          label: 'Family lunch',
          sub: 'This will not help',
          scores: { category: { family: 1 }, energy: { statement: 1 } },
        },
      ],
    },
    {
      id: 'risk',
      title: 'How much social risk can you tolerate?',
      text: 'Some of these hats are louder than others.',
      answers: [
        {
          label: 'Low',
          sub: 'Funny, but not too much',
          scores: { risk: { low: 2 }, energy: { wearable: 1 } },
        },
        {
          label: 'Medium',
          sub: 'I want comments, not a scene',
          scores: { risk: { medium: 2 }, energy: { wearable: 1 } },
        },
        {
          label: 'High',
          sub: 'I want the table to read it',
          scores: { risk: { high: 2 }, energy: { statement: 1 } },
        },
        {
          label: 'Maximum',
          sub: 'I am the content',
          scores: { risk: { maximum: 2 }, energy: { absurd: 1 } },
        },
      ],
    },
    {
      id: 'humour',
      title: "What's your sense of humour?",
      text: 'Pick the one closest to how your friends would describe you.',
      answers: [
        {
          label: 'Dry and understated',
          sub: 'You let the silence do the work',
          scores: { tone: { dry: 2 } },
        },
        {
          label: 'Loud and chaotic',
          sub: 'Timing is a myth',
          scores: { tone: { chaotic: 2 } },
        },
        {
          label: 'Deadpan and blunt',
          sub: 'No punchline, just facts',
          scores: { tone: { deadpan: 2 } },
        },
        {
          label: 'A little flirty',
          sub: 'Slightly inappropriate, mostly on purpose',
          scores: { tone: { flirty: 2 } },
        },
      ],
    },
    {
      id: 'loudness',
      title: 'How loud do you want the cap to be?',
      text: 'Pick a volume.',
      answers: [
        {
          label: 'Wearable',
          sub: 'Something people compliment',
          scores: { energy: { wearable: 2 } },
        },
        {
          label: 'Statement',
          sub: 'Something people notice',
          scores: { energy: { statement: 2 } },
        },
        {
          label: 'Absurd',
          sub: 'Something people photograph',
          scores: { energy: { absurd: 2 } },
        },
        {
          label: 'Confrontational',
          sub: 'Something people react to',
          scores: { energy: { confrontational: 2 } },
        },
      ],
    },
  ],
};
